import React, { useState } from 'react';
import { LifeBuoy, X, Send, CheckCircle2, Loader2 } from 'lucide-react';
import { api } from '../lib/api';

const HELP_OPTIONS = [
  { id: 'login', label: 'No puedo iniciar sesión / acceder a mi cuenta' },
  { id: 'invitation', label: 'El enlace de invitación no funciona o expiró' },
  { id: 'photo', label: 'No puedo subir mi foto' },
  { id: 'profile', label: 'No sé cómo llenar o guardar mi perfil' },
  { id: 'other', label: 'Otro / no estoy seguro' },
];

/**
 * "Contactar a soporte" button + modal shown on the registration pages.
 *
 * Always visible (so users can reach support even without an error). When the
 * user submits, we attach the current error message and the captured click log
 * so the admin can diagnose what went wrong.
 *
 * Props:
 *  - source: 'register' | 'register-confirm'
 *  - defaultEmail: prefill for the email field
 *  - getDiagnostics: () => ({ error_message, click_log })
 */
export default function SupportContactButton({ source = 'register', defaultEmail = '', defaultName = '', getDiagnostics }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(defaultEmail);
  const [name, setName] = useState(defaultName);
  const [message, setMessage] = useState('');
  const [helpTypes, setHelpTypes] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState('');

  function toggleHelp(id) {
    setHelpTypes((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  function openModal() {
    setEmail(defaultEmail || email);
    setName(defaultName || name);
    setSent(false);
    setErr('');
    setOpen(true);
  }

  async function submit(e) {
    e.preventDefault();
    setSending(true);
    setErr('');
    try {
      const diag = (typeof getDiagnostics === 'function' && getDiagnostics()) || {};
      await api.post('/support/tickets', {
        email: (email || '').trim(),
        name: (name || '').trim(),
        message: (message || '').trim(),
        help_types: helpTypes,
        error_message: diag.error_message || '',
        click_log: diag.click_log || [],
        source,
        page_url: window.location.href,
        user_agent: navigator.userAgent,
      });
      setSent(true);
    } catch {
      setErr('No pudimos enviar tu solicitud. Inténtalo de nuevo en un momento.');
    }
    setSending(false);
  }

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        data-testid="support-contact-open-btn"
        className="inline-flex items-center gap-2 text-sm font-medium text-charcoal-500 hover:text-cmic-700 transition-colors"
      >
        <LifeBuoy size={15} /> ¿Tienes problemas? Contactar a soporte
      </button>

      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" data-testid="support-modal">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2 font-semibold text-charcoal-800">
                <LifeBuoy size={18} className="text-cmic-600" /> Contactar a soporte
              </div>
              <button onClick={() => setOpen(false)} data-testid="support-modal-close" className="p-1 text-charcoal-400 hover:text-charcoal-700">
                <X size={18} />
              </button>
            </div>

            {sent ? (
              <div className="p-6 text-center" data-testid="support-modal-success">
                <div className="w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 size={28} />
                </div>
                <h3 className="font-semibold text-charcoal-800 mb-1">¡Solicitud enviada!</h3>
                <p className="text-sm text-charcoal-600 mb-5">Recibimos tu reporte. Te contactaremos pronto para ayudarte a completar tu registro.</p>
                <button onClick={() => setOpen(false)} data-testid="support-modal-done" className="inline-flex items-center justify-center gap-2 bg-cmic-600 hover:bg-cmic-700 text-white font-semibold rounded-xl px-6 py-2.5 text-sm">
                  Cerrar
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="p-5 space-y-4 max-h-[78vh] overflow-y-auto" data-testid="support-modal-form">
                <p className="text-sm text-charcoal-600">
                  Cuéntanos qué pasó. Adjuntaremos automáticamente los detalles técnicos para ayudarte más rápido.
                </p>
                <div>
                  <label className="block text-xs font-semibold text-charcoal-700 mb-2">¿Con qué necesitas ayuda? (marca lo que aplique)</label>
                  <div className="space-y-2">
                    {HELP_OPTIONS.map((opt) => (
                      <label key={opt.id} className="flex items-start gap-2.5 text-sm text-charcoal-700 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={helpTypes.includes(opt.id)}
                          onChange={() => toggleHelp(opt.id)}
                          data-testid={`support-help-${opt.id}`}
                          className="mt-0.5 rounded border-gray-300 text-cmic-600 focus:ring-cmic-500"
                        />
                        <span>{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-charcoal-700 mb-1.5">¿Qué problema tuviste? Escríbelo aquí</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={4}
                    data-testid="support-message-input"
                    placeholder="Ej.: No puedo subir mi foto / Necesito ayuda para guardar mi perfil…"
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500 resize-y"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-charcoal-700 mb-1.5">Tu nombre completo</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="support-name-input"
                    placeholder="Ej.: María López García"
                    className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-charcoal-700 mb-1.5">Tu correo electrónico</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="support-email-input"
                    placeholder="tu-correo@…"
                    className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
                  />
                </div>
                {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2" data-testid="support-modal-error">{err}</div>}
                <button
                  type="submit"
                  disabled={sending || !email.trim()}
                  data-testid="support-submit-btn"
                  className="w-full flex items-center justify-center gap-2 bg-cmic-600 hover:bg-cmic-700 text-white font-semibold rounded-xl px-6 py-3 text-sm transition-all disabled:opacity-50"
                >
                  {sending ? <Loader2 size={16} className="animate-spin" /> : <><Send size={15} /> Enviar a soporte</>}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
